import {
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Chip as MUIChip,
  Theme,
} from '@material-ui/core'
import Drawer, { Heading } from './Drawer'
import { useReducer, useEffect } from 'react'
import { ReligiousAffiliation, UIFilters, unknownLocation } from '../types'
import { TITLE_1_PROGRAM_OPTIONS } from '../title-one'
import { isValid, startOfWeek, addMonths, endOfMonth, endOfWeek, startOfMonth, isAfter, format } from 'date-fns'
import { MuiPickersUtilsProvider, KeyboardDatePicker, DatePickerView } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { makeStyles, styled } from '@material-ui/core/styles'
import getFilterCount from '../filter-state-count'
import LocationFilter, { unknownLocationOption } from './FilterLocation'
import FilterReligiousAffiliation from './FilterReligiousAffiliation'

type State = {
  formState: UIFilters
}

type Action =
  | {
      type: 'update-form-state'
      formState: Partial<UIFilters>
    }
  | {
      type: 'reset-form-state'
    }
  | {
      type: 'select-visit-availability-option'
      option?: 'this-week' | 'this-month' | 'next-month' | 'custom'
    }
  | {
      type: 'update-visibility-dates'
      visitAvailabilityDates: {
        startDate?: Date | null
        endDate?: Date | null
      }
    }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'update-form-state': {
      return { ...state, formState: { ...state.formState, ...action.formState } }
    }
    case 'reset-form-state':
      return {
        ...state,
        formState: {},
      }
    case 'select-visit-availability-option':
      if (!action.option) {
        return {
          ...state,
          formState: {
            ...state.formState,
            visitRange: undefined,
          },
        }
      }

      return {
        ...state,
        formState: {
          ...state.formState,
          visitRange: getVisitVailabilityFromOption(action.option),
        },
      }
    case 'update-visibility-dates':
      return {
        ...state,
        formState: {
          ...state.formState,
          visitRange: {
            type: 'custom',
            ...state.formState.visitRange,
            startDate: dateOrFallback(action.visitAvailabilityDates.startDate, state.formState.visitRange?.startDate),
            endDate: dateOrFallback(action.visitAvailabilityDates.endDate, state.formState.visitRange?.endDate),
          },
        },
      }
    default:
      return state
  }
}

const SectionBox = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  '&:last-child': {
    borderBottom: 'none',
  },
}))

const commonDatePickerProps = {
  views: ['month', 'date'] as DatePickerView[],
  disableFuture: false,
  disablePast: true,
  autoOk: true,
  clearable: true,
  inputVariant: 'outlined' as const,
  format: 'MM/dd/yyyy',
  variant: 'dialog' as const,
  invalidDateMessage: 'Invalid date format',
  KeyboardButtonProps: { 'aria-label': 'change date' },
}

const parseNumeric = (v: string): number | undefined => {
  const n = parseInt(v, 10)
  return isNaN(n) ? undefined : n
}

const Chip = styled(MUIChip)(() => ({
  maxWidth: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}))

type FilterChip = {
  key: keyof UIFilters
  label: string
}

const VIRTUAL_LABELS = {
  virtual: 'Virtual Only',
  inPerson: 'In Person Only',
}

type VisitRangeLabels = {
  type: {
    [K in NonNullable<UIFilters['visitRange']>['type']]: string
  }
  date: {
    from: string
    until: string
    range: string
  }
}

type SchoolTypeLabels = {
  [K in NonNullable<UIFilters['schoolType']>]: string
}

type RangeLabels = {
  label: string
  range: string
  min: string
  max: string
}

const FILTER_LABELS: {
  [TKey in keyof Omit<
    UIFilters,
    'virtual' | 'text' | 'locations' | 'religiousAffiliation'
  >]-?: TKey extends 'visitRange'
    ? VisitRangeLabels
    : TKey extends 'schoolType'
      ? SchoolTypeLabels
      : TKey extends 'title1Program'
        ? typeof TITLE_1_PROGRAM_OPTIONS
        : TKey extends 'pctFreeReducedLunch' | 'classSize'
          ? RangeLabels
          : TKey extends 'isActive' | 'isTitle1Eligible'
            ? string
            : never
} = {
  visitRange: {
    type: {
      'this-week': 'This Week',
      'this-month': 'This Month',
      'next-month': 'Next Month',
      custom: 'Custom Range',
    },
    date: {
      from: 'From',
      until: 'Until',
      range: '{start} - {end}',
    },
  },
  schoolType: {
    public: 'Public Schools',
    private: 'Private Schools',
  },
  title1Program: TITLE_1_PROGRAM_OPTIONS,
  pctFreeReducedLunch: {
    label: 'Free/Reduced Lunch',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  classSize: {
    label: 'Class Size',
    range: '{min} - {max}',
    min: '≥{value}',
    max: '≤{value}',
  },
  isActive: 'Only Scoir schools',
  isTitle1Eligible: 'Title 1 Eligible Schools',
}

type ChipGenerator = (formState: UIFilters) => FilterChip | undefined

const FILTER_CHIP_GENERATORS: Record<keyof UIFilters, ChipGenerator> = {
  locations: (formState) => {
    if (formState.locations === undefined || formState.locations.length === 0) return undefined
    const locations = formState.locations.map((loc) => (loc == unknownLocation ? unknownLocationOption.name : loc))
    return {
      key: 'locations',
      label: `States: ${locations.join(', ')}`,
    }
  },
  religiousAffiliation: (formState) => {
    if (formState.religiousAffiliation === undefined || formState.religiousAffiliation.length === 0) return undefined
    return {
      key: 'religiousAffiliation',
      label: `Religious Affiliation: ${formState.religiousAffiliation
        .map((v) => (v === '' ? 'Unknown' : v))
        .join(', ')}`,
    }
  },
  virtual: (formState) => {
    if (formState.virtual === undefined) return undefined
    return {
      key: 'virtual',
      label: `Visit Type: ${formState.virtual ? VIRTUAL_LABELS.virtual : VIRTUAL_LABELS.inPerson}`,
    }
  },
  visitRange: (formState) => {
    if (!formState.visitRange) return undefined

    const { type, startDate, endDate } = formState.visitRange
    if (type === 'custom') {
      if (startDate && endDate) {
        return {
          key: 'visitRange',
          label: `Visits: ${FILTER_LABELS.visitRange.date.range
            .replace('{start}', format(startDate, 'MMM d'))
            .replace('{end}', format(endDate, 'MMM d, yyyy'))}`,
        }
      } else if (startDate) {
        return {
          key: 'visitRange',
          label: `Visits: ${FILTER_LABELS.visitRange.date.from} ${format(startDate, 'MMM d, yyyy')}`,
        }
      } else if (endDate) {
        return {
          key: 'visitRange',
          label: `Visits: ${FILTER_LABELS.visitRange.date.until} ${format(endDate, 'MMM d, yyyy')}`,
        }
      }
    } else if (type) {
      return {
        key: 'visitRange',
        label: `Visits: ${FILTER_LABELS.visitRange.type[type]}`,
      }
    }
    return undefined
  },

  schoolType: (formState) => {
    if (!formState.schoolType) return undefined
    return {
      key: 'schoolType',
      label: `School Type: ${FILTER_LABELS.schoolType[formState.schoolType]}`,
    }
  },

  title1Program: (formState) => {
    if (!formState.title1Program) return undefined
    const program = FILTER_LABELS.title1Program[formState.title1Program]
    if (!program) return undefined
    return {
      key: 'title1Program',
      label: `Title 1 Program: ${program.label}`,
    }
  },

  pctFreeReducedLunch: (formState) => {
    if (!formState.pctFreeReducedLunch) return undefined
    const { min, max } = formState.pctFreeReducedLunch
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.pctFreeReducedLunch
    if (min && max) {
      return {
        key: 'pctFreeReducedLunch',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (min) {
      return {
        key: 'pctFreeReducedLunch',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (max) {
      return {
        key: 'pctFreeReducedLunch',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  classSize: (formState) => {
    if (!formState.classSize) return undefined
    const { min, max } = formState.classSize
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.classSize
    if (min && max) {
      return {
        key: 'classSize',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (min) {
      return {
        key: 'classSize',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (max) {
      return {
        key: 'classSize',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  isActive: (formState) => {
    if (formState.isActive === undefined) return undefined
    return {
      key: 'isActive',
      label: `Viewing: ${FILTER_LABELS.isActive}`,
    }
  },

  isTitle1Eligible: (formState) => {
    if (formState.isTitle1Eligible === undefined) return undefined
    return {
      key: 'isTitle1Eligible',
      label: `Title 1 Eligibility: ${formState.isTitle1Eligible ? 'Eligible' : 'Not Eligible'}`,
    }
  },

  text: (formState) => {
    if (!formState.text) return undefined
    return {
      key: 'text',
      label: `Search: ${formState.text}`,
    }
  },
}

function getFilterChips(formState: UIFilters, onRemove: (key: keyof UIFilters) => void) {
  const chips: FilterChip[] = []
  Object.entries(FILTER_CHIP_GENERATORS).forEach(([, generator]) => {
    const chip = generator(formState)
    if (chip) {
      chips.push(chip)
    }
  })

  return chips.map((chip) => (
    <Chip
      key={chip.key}
      label={chip.label}
      onDelete={() => onRemove(chip.key)}
      color="primary"
      variant="outlined"
      size="small"
    />
  ))
}

const useStyles = makeStyles((theme: Theme) => ({
  appliedFilterSection: {
    backgroundColor: theme.palette.pageBackground?.main,
  },
}))

export default function FilterDrawer({
  isOpen,
  onClose,
  onClickApply,
  defaultFilters,
  showPremiumFilters,
}: {
  isOpen: boolean
  onClose: () => void
  onClickApply: (f: UIFilters) => void
  defaultFilters: UIFilters
  showPremiumFilters: boolean
}) {
  const classes = useStyles()
  const [state, dispatch] = useReducer(reducer, {
    formState: {
      ...defaultFilters,
    },
  })

  useEffect(() => {
    dispatch({
      type: 'update-form-state',
      formState: defaultFilters,
    })
  }, [defaultFilters])

  const count = getFilterCount(state.formState)

  const handleRemoveFilter = (key: keyof UIFilters) => {
    dispatch({
      type: 'update-form-state',
      formState: { [key]: undefined },
    })
  }

  const errors = validate(state.formState)
  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      maxWidth={460}
      heading={
        <Heading
          variant="h6"
          component="h3"
        >
          Filters
        </Heading>
      }
      bodyPadding={false}
      footer={
        <Box
          padding={2}
          display="flex"
          justifyContent="space-between"
        >
          <Button
            variant="text"
            size="large"
            color="primary"
            onClick={() => {
              dispatch({ type: 'reset-form-state' })
            }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onClickApply(state.formState)
            }}
            disabled={Object.keys(errors).length > 0}
          >
            Apply Filters
          </Button>
        </Box>
      }
    >
      <Box>
        {count > 0 && (
          <SectionBox className={classes.appliedFilterSection}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                component="div"
                variant="h6"
              >
                Applied Filters
              </Typography>
              <Button
                variant="text"
                size="large"
                color="primary"
                onClick={() => {
                  dispatch({ type: 'reset-form-state' })
                }}
              >
                Clear All
              </Button>
            </Box>
            <Box
              mt={2}
              display="flex"
              flexWrap="wrap"
              style={{ gap: 8 }}
            >
              {getFilterChips(state.formState, handleRemoveFilter)}
            </Box>
          </SectionBox>
        )}
        <SectionBox>
          <Typography
            component="div"
            variant="h6"
          >
            Visits
          </Typography>
          <Box mt={2}>
            <Typography
              variant="body1"
              gutterBottom
            >
              Availability
            </Typography>
            <ButtonGroup
              disableFocusRipple
              color="primary"
            >
              <Button
                variant={state.formState.visitRange?.type === 'this-week' ? 'contained' : 'outlined'}
                onClick={() =>
                  state.formState.visitRange?.type === 'this-week'
                    ? dispatch({ type: 'select-visit-availability-option', option: undefined })
                    : dispatch({ type: 'select-visit-availability-option', option: 'this-week' })
                }
              >
                This Week
              </Button>
              <Button
                variant={state.formState.visitRange?.type === 'this-month' ? 'contained' : 'outlined'}
                onClick={() =>
                  state.formState.visitRange?.type === 'this-month'
                    ? dispatch({ type: 'select-visit-availability-option', option: undefined })
                    : dispatch({ type: 'select-visit-availability-option', option: 'this-month' })
                }
              >
                This Month
              </Button>
              <Button
                variant={state.formState.visitRange?.type === 'next-month' ? 'contained' : 'outlined'}
                onClick={() =>
                  state.formState.visitRange?.type === 'next-month'
                    ? dispatch({ type: 'select-visit-availability-option', option: undefined })
                    : dispatch({ type: 'select-visit-availability-option', option: 'next-month' })
                }
              >
                Next Month
              </Button>
              <Button
                variant={state.formState.visitRange?.type === 'custom' ? 'contained' : 'outlined'}
                onClick={() =>
                  state.formState.visitRange?.type === 'custom'
                    ? dispatch({ type: 'select-visit-availability-option', option: undefined })
                    : dispatch({ type: 'select-visit-availability-option', option: 'custom' })
                }
              >
                Custom
              </Button>
            </ButtonGroup>
            {state.formState.visitRange?.type === 'custom' && (
              <Box
                mt={2}
                display="flex"
                alignItems={'start'}
                gridGap={8}
              >
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <Box>
                    <KeyboardDatePicker
                      id="visit-availability-start-date"
                      label="From"
                      minDate={0}
                      minDateMessage="Cannot be in the past"
                      maxDateMessage="Must be before To date"
                      value={state.formState.visitRange?.startDate || null}
                      onChange={(date) => {
                        if (isValid(date) || date === null) {
                          dispatch({
                            type: 'update-visibility-dates',
                            visitAvailabilityDates: {
                              startDate: date,
                            },
                          })
                        }
                      }}
                      {...commonDatePickerProps}
                    />
                  </Box>
                  <Box>
                    <KeyboardDatePicker
                      id="visit-availability-end-date"
                      label="To"
                      minDateMessage="Must be after From date"
                      minDate={state.formState.visitRange?.startDate}
                      value={state.formState.visitRange?.endDate || null}
                      onChange={(date) => {
                        if (isValid(date) || date === null) {
                          dispatch({
                            type: 'update-visibility-dates',
                            visitAvailabilityDates: {
                              endDate: date,
                            },
                          })
                        }
                      }}
                      {...commonDatePickerProps}
                    />
                  </Box>
                </MuiPickersUtilsProvider>
              </Box>
            )}
            <Box mt={4}>
              <Typography
                variant="body1"
                gutterBottom
              >
                Type
              </Typography>
              <ButtonGroup
                disableFocusRipple
                color="primary"
              >
                <Button
                  variant={state.formState.virtual === false ? 'contained' : 'outlined'}
                  onClick={() => {
                    dispatch({
                      type: 'update-form-state',
                      formState: {
                        virtual:
                          state.formState.virtual === undefined || state.formState.virtual === true ? false : undefined,
                      },
                    })
                  }}
                >
                  In Person
                </Button>
                <Button
                  variant={state.formState.virtual === true ? 'contained' : 'outlined'}
                  onClick={() =>
                    state.formState.virtual === undefined || state.formState.virtual === false
                      ? dispatch({ type: 'update-form-state', formState: { virtual: true } })
                      : dispatch({ type: 'update-form-state', formState: { virtual: undefined } })
                  }
                >
                  Virtual
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
        </SectionBox>
        <SectionBox>
          <Typography
            component="div"
            variant="h6"
          >
            School Details
          </Typography>
          <Box mt={2}>
            <ButtonGroup
              disableFocusRipple
              color="primary"
            >
              <Button
                variant={state.formState.schoolType === 'public' ? 'contained' : 'outlined'}
                onClick={() =>
                  state.formState.schoolType === 'public'
                    ? dispatch({ type: 'update-form-state', formState: { schoolType: undefined } })
                    : dispatch({ type: 'update-form-state', formState: { schoolType: 'public' } })
                }
              >
                Public
              </Button>
              <Button
                variant={state.formState.schoolType === 'private' ? 'contained' : 'outlined'}
                onClick={() =>
                  state.formState.schoolType === 'private'
                    ? dispatch({ type: 'update-form-state', formState: { schoolType: undefined } })
                    : dispatch({ type: 'update-form-state', formState: { schoolType: 'private' } })
                }
              >
                Private
              </Button>
            </ButtonGroup>
          </Box>
          <Box mt={4}>
            <LocationFilter
              selectedLocations={state.formState.locations}
              onChangeLocation={(locations) => {
                dispatch({
                  type: 'update-form-state',
                  formState: { locations },
                })
              }}
            />
          </Box>
          <Box mt={4}>
            <FilterReligiousAffiliation
              religiousAffiliation={state.formState.religiousAffiliation || []}
              setReligiousAffiliation={(affiliations: ReligiousAffiliation[]) => {
                dispatch({
                  type: 'update-form-state',
                  formState: { religiousAffiliation: affiliations.length > 0 ? affiliations : undefined },
                })
              }}
            />
          </Box>
          <Box mt={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={state.formState.isActive}
                  onChange={(_, checked) =>
                    dispatch({
                      type: 'update-form-state',
                      formState: { isActive: checked ? true : undefined },
                    })
                  }
                />
              }
              label="Only show Scoir schools"
            />
          </Box>
          <Box mt={4}>
            <Typography
              variant="body1"
              gutterBottom
            >
              Title 1 Eligibility
            </Typography>
            <ButtonGroup
              disableFocusRipple
              color="primary"
            >
              <Button
                variant={state.formState.isTitle1Eligible === true ? 'contained' : 'outlined'}
                onClick={() =>
                  state.formState.isTitle1Eligible === true
                    ? dispatch({ type: 'update-form-state', formState: { isTitle1Eligible: undefined } })
                    : dispatch({ type: 'update-form-state', formState: { isTitle1Eligible: true } })
                }
              >
                Eligible
              </Button>
              <Button
                variant={state.formState.isTitle1Eligible === false ? 'contained' : 'outlined'}
                onClick={() =>
                  state.formState.isTitle1Eligible === false
                    ? dispatch({ type: 'update-form-state', formState: { isTitle1Eligible: undefined } })
                    : dispatch({ type: 'update-form-state', formState: { isTitle1Eligible: false } })
                }
              >
                Not Eligible
              </Button>
            </ButtonGroup>
          </Box>

          <PremiumFilter enabled={showPremiumFilters}>
            <Box mt={4}>
              <Typography variant="body1">Title 1 Program</Typography>

              <Box width={250}>
                <Select
                  id="title1Program"
                  fullWidth
                  variant="outlined"
                  value={state.formState.title1Program ?? 'empty'}
                  onChange={(e) => {
                    const t1p = e.target.value
                    if (t1p === 'swp' || t1p === 'tas') {
                      dispatch({
                        type: 'update-form-state',
                        formState: { title1Program: t1p },
                      })
                      return
                    }
                    dispatch({
                      type: 'update-form-state',
                      formState: { title1Program: undefined },
                    })
                  }}
                >
                  <MenuItem value={'empty'}>Select Program</MenuItem>
                  {Object.values(TITLE_1_PROGRAM_OPTIONS).map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </PremiumFilter>
          <PremiumFilter enabled={showPremiumFilters}>
            <Box mt={4}>
              <Typography variant="body1">% Free or Reduced Lunch</Typography>
              <Box
                display="flex"
                gridGap={16}
                pt={1}
              >
                <Box flexBasis="50%">
                  <TextField
                    fullWidth
                    id="min-pctFreeReducedLunch"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="At least"
                    value={numericValueOrDefault(state.formState.pctFreeReducedLunch?.min)}
                    inputProps={{ min: 1, max: state.formState.pctFreeReducedLunch?.min || 100 }}
                    onChange={(e) => {
                      let value = parseNumeric(e.target.value)
                      if (value && value > 100) {
                        value = 100
                      }
                      dispatch({
                        type: 'update-form-state',
                        formState: {
                          pctFreeReducedLunch: {
                            ...state.formState.pctFreeReducedLunch,
                            min: e.target.value == '' ? undefined : value,
                          },
                        },
                      })
                    }}
                    error={!!errors.pctFreeReducedLunch}
                  />
                </Box>
                <Box flexBasis="50%">
                  <TextField
                    fullWidth
                    id="max-pctFreeReducedLunch"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="Up to"
                    value={numericValueOrDefault(state.formState.pctFreeReducedLunch?.max)}
                    inputProps={{ min: state.formState.pctFreeReducedLunch?.max || 0, max: 100 }}
                    onChange={(e) => {
                      let value = parseNumeric(e.target.value)
                      if (value && value > 100) {
                        value = 100
                      }

                      dispatch({
                        type: 'update-form-state',
                        formState: {
                          pctFreeReducedLunch: {
                            ...state.formState.pctFreeReducedLunch,
                            max: e.target.value == '' ? undefined : value,
                          },
                        },
                      })
                    }}
                    error={!!errors.pctFreeReducedLunch}
                  />
                </Box>
              </Box>
              <FormHelperText error={!!errors.pctFreeReducedLunch}>
                {errors.pctFreeReducedLunch?.helperText}
              </FormHelperText>
            </Box>
          </PremiumFilter>
          <PremiumFilter enabled={showPremiumFilters}>
            <Box mt={4}>
              <Typography variant="body1">Senior Class Size</Typography>
              <Box
                display="flex"
                gridGap={16}
                pt={1}
              >
                <Box flexBasis="50%">
                  <TextField
                    fullWidth
                    id="min-class-size"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="At least"
                    value={numericValueOrDefault(state.formState.classSize?.min)}
                    inputProps={{ min: 1, max: state.formState.classSize?.max }}
                    onChange={(e) => {
                      const value = parseNumeric(e.target.value)
                      dispatch({
                        type: 'update-form-state',
                        formState: {
                          classSize: {
                            ...state.formState.classSize,
                            min: value,
                          },
                        },
                      })
                    }}
                    error={!!errors.classSize}
                  />
                </Box>
                <Box flexBasis="50%">
                  <TextField
                    fullWidth
                    id="max-class-size"
                    variant="outlined"
                    color="primary"
                    type="number"
                    label="Up to"
                    value={numericValueOrDefault(state.formState.classSize?.max)}
                    inputProps={{ min: state.formState.classSize?.min || 0 }}
                    onChange={(e) => {
                      const value = parseNumeric(e.target.value)
                      dispatch({
                        type: 'update-form-state',
                        formState: {
                          classSize: {
                            ...state.formState.classSize,
                            max: value,
                          },
                        },
                      })
                    }}
                    error={!!errors.classSize}
                  />
                </Box>
              </Box>
            </Box>
            <FormHelperText error={!!errors.classSize}>{errors.classSize?.helperText}</FormHelperText>
          </PremiumFilter>
        </SectionBox>
      </Box>
    </Drawer>
  )
}

const numericValueOrDefault = (v?: number, def: string = ''): number | string => {
  return v === undefined ? def : v
}

type ValidationResult = {
  [K in keyof UIFilters]: {
    helperText: string
  }
}

function validate(state: UIFilters): Partial<ValidationResult> {
  if (state.pctFreeReducedLunch !== undefined) {
    if (state.pctFreeReducedLunch.min !== undefined && state.pctFreeReducedLunch.min < 1) {
      return {
        pctFreeReducedLunch: {
          helperText: 'Minimum must be greater than 0',
        },
      }
    }

    if (state.pctFreeReducedLunch.max !== undefined && state.pctFreeReducedLunch.max < 1) {
      return {
        pctFreeReducedLunch: {
          helperText: 'Maximum must be greater than 0',
        },
      }
    }

    if (
      state.pctFreeReducedLunch.min !== undefined &&
      state.pctFreeReducedLunch.max !== undefined &&
      state.pctFreeReducedLunch.min >= state.pctFreeReducedLunch.max
    ) {
      return {
        pctFreeReducedLunch: {
          helperText: 'Must be greater than "At least"',
        },
      }
    }
  }
  if (state.classSize !== undefined) {
    if (state.classSize?.min !== undefined && state.classSize?.min < 1) {
      return {
        classSize: {
          helperText: 'Minimum must be greater than 0',
        },
      }
    }

    if (state.classSize?.max !== undefined && state.classSize?.max < 1) {
      return {
        classSize: {
          helperText: 'Maximum must be greater than 0',
        },
      }
    }

    if (
      state.classSize?.min !== undefined &&
      state.classSize?.max !== undefined &&
      state.classSize?.min >= state.classSize?.max
    ) {
      return {
        classSize: {
          helperText: 'Must be greater than "At least"',
        },
      }
    }
  }

  if (
    state.visitRange?.startDate &&
    state.visitRange?.endDate &&
    isAfter(state.visitRange?.startDate, state.visitRange?.endDate)
  ) {
    return {
      visitRange: {
        helperText: 'Visits To must be after From',
      },
    }
  }
  if (
    state.visitRange?.type === 'custom' &&
    state.visitRange?.startDate === undefined &&
    state.visitRange?.endDate === undefined
  ) {
    return {
      visitRange: {
        helperText: 'Please select a date range',
      },
    }
  }

  return {}
}

export type AvailabilityOption = NonNullable<UIFilters['visitRange']>['type']

function getVisitVailabilityFromOption(option: AvailabilityOption | undefined): UIFilters['visitRange'] {
  const now = new Date()
  switch (option) {
    case 'this-week': {
      const startDate = startOfWeek(now)
      const endDate = endOfWeek(now)
      return { type: option, startDate, endDate }
    }
    case 'this-month': {
      const startDate = startOfMonth(now)
      const endDate = endOfMonth(now)
      return { type: option, startDate, endDate }
    }
    case 'next-month': {
      const nextMonth = addMonths(now, 1)
      const startDate = startOfMonth(nextMonth)
      const endDate = endOfMonth(nextMonth)
      return { type: option, startDate, endDate }
    }
    case 'custom':
      return { type: option }
    default:
      return undefined
  }
}
const dateOrFallback = (date: Date | null | undefined, fallback: Date | undefined): Date | undefined => {
  if (date === null) return undefined
  return date || fallback
}

function PremiumFilter({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  if (!enabled) return null
  return <>{children}</>
}
